/**
 * secure-score-matrix.ts — placement helper for the impact-vs-cost scatter
 * matrix on the Microsoft Secure Score sub-page (§7.3).
 *
 * Returns SVG-friendly normalized coordinates (0..1) plus an editorial tone
 * key driven by `userImpact`. The component layer reads `x` / `y` for `<circle>`
 * positioning and `tone` for fill color.
 *
 * Coordinate system is SVG-native: `y = 0` is the top of the chart, `y = 1` is
 * the bottom. Larger point gain → smaller `y` (dot sits higher).
 */

import type { SecureScoreControlProfile } from '@/types/secureScore'

/** 'l' = low impact, 'm' = moderate, 'h' = high. */
export type MatrixTone = 'l' | 'm' | 'h'

export interface MatrixPlacement {
  /** 0..1 SVG x. 0.18 / 0.50 / 0.82 for Low / Moderate / High cost. */
  x: number
  /** 0..1 SVG y, padded away from edges. Top-left origin → larger gap = smaller y. */
  y: number
  /** Editorial tone key for the dot fill. */
  tone: MatrixTone
  /** Points to gain (rec.maxScore - rec.achievedScore), capped at 10. */
  gap: number
}

/** Three-bucket enum shared by `implementationCost` and `userImpact`. */
export type CostImpactBucket = 'Low' | 'Moderate' | 'High'

/** x-axis position per cost bucket. Padded to keep dots clear of axis labels. */
export const X_BY_COST: Readonly<Record<CostImpactBucket, number>> = {
  Low: 0.18,
  Moderate: 0.5,
  High: 0.82,
}

/** y-axis tone key per impact bucket. Renderer maps this to a fill color. */
export const TONE_BY_IMPACT: Readonly<Record<CostImpactBucket, MatrixTone>> = {
  Low: 'l',
  Moderate: 'm',
  High: 'h',
}

function isKnownBucket(value: string | null | undefined): value is CostImpactBucket {
  return value === 'Low' || value === 'Moderate' || value === 'High'
}

/** Graph occasionally returns empty strings or values outside the documented
 *  enum. Coerce anything we don't recognise to 'Moderate' so the dot lands in
 *  the centre of the chart instead of falling off the left edge (cx=NaN). */
function asBucket(value: string | null | undefined): CostImpactBucket {
  return isKnownBucket(value) ? value : 'Moderate'
}

/**
 * Returns true only when Microsoft has tagged BOTH `implementationCost` and
 * `userImpact` with one of the documented buckets. Recs that fail this check
 * are excluded from the scatter matrix and surfaced via a footnote so we don't
 * pretend to know placements Microsoft hasn't given us.
 */
export function isMatrixClassified(rec: SecureScoreControlProfile): boolean {
  return isKnownBucket(rec.implementationCost) && isKnownBucket(rec.userImpact)
}

/** Anything beyond 10 pts gets pinned to the top of the chart so a single
 *  outlier rec doesn't compress the rest of the spread into a horizontal band. */
const GAP_CAP = 10

/** Pad y-axis 7% from top and 8% from bottom so dots don't kiss the axis lines. */
const Y_PAD_TOP = 0.07
const Y_RANGE = 0.85

/**
 * Map a Secure Score recommendation to its scatter-matrix coordinates.
 *
 * Defaults when the rec is missing a field:
 *  - `implementationCost` → 'Moderate' (centre of x-axis)
 *  - `userImpact`         → 'Moderate' (mid tone)
 *  - missing scores       → gap of 0 (dot sits at the bottom)
 */
export function placeOnMatrix(rec: SecureScoreControlProfile): MatrixPlacement {
  const cost = asBucket(rec.implementationCost)
  const impact = asBucket(rec.userImpact)

  const rawGap = (rec.maxScore ?? 0) - (rec.achievedScore ?? 0)
  const gap = Math.min(GAP_CAP, Math.max(0, rawGap))

  // Scale gap to 0..1 of the visible range, then invert (top-left origin SVG).
  const yPct = gap / GAP_CAP
  const y = (1 - yPct) * Y_RANGE + Y_PAD_TOP

  return {
    x: X_BY_COST[cost],
    y,
    tone: TONE_BY_IMPACT[impact],
    gap,
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

export const __TEST_ONLY__ = {
  GAP_CAP,
}
